import type { MaterialStore } from '@api/warehouse-ops';
import { Button, Group, Modal, Radio, Select, Stack, Text } from '@mantine/core';
import { type FC, useEffect, useState } from 'react';

export type GrnKindPick = { kind: 'fg' } | { kind: 'material'; warehouseId: string };

interface Props {
    opened: boolean;
    stores: MaterialStore[];
    onClose: () => void;
    onContinue: (pick: GrnKindPick) => void;
}

/**
 * Step one of Add GRN: what is being received, and for raw and packaging material, which store.
 * Finished goods carry on to the existing form; material opens the receive grid.
 */
const CreateGrnTypeModal: FC<Props> = ({ opened, stores, onClose, onContinue }) => {
    const [kind, setKind] = useState<'fg' | 'material'>('material');
    const [warehouseId, setWarehouseId] = useState<string | null>(null);

    // Every opening starts fresh, on the only store when there is just one.
    useEffect(() => {
        if (!opened) return;
        setKind('material');
        setWarehouseId(stores.length === 1 ? stores[0].warehouseId : null);
    }, [opened, stores]);

    const ready = kind === 'fg' || !!warehouseId;

    return (
        <Modal opened={opened} onClose={onClose} title="Add GRN" centered size="md">
            <Stack gap="md">
                <Radio.Group
                    label="What is being received?"
                    value={kind}
                    onChange={(value) => setKind(value as 'fg' | 'material')}
                >
                    <Stack gap="xs" mt="xs">
                        <Radio value="fg" label="Finished goods" />
                        <Radio value="material" label="Raw & packing material (RM/PM)" />
                    </Stack>
                </Radio.Group>

                {kind === 'material' && (
                    <Select
                        label="Warehouse"
                        description="Where it arrived. Each line gets its QC verdict as it is received."
                        searchable
                        data={stores.map((store) => ({ value: store.warehouseId, label: store.name }))}
                        value={warehouseId}
                        onChange={setWarehouseId}
                        allowDeselect={false}
                        comboboxProps={{ position: 'bottom-start' }}
                    />
                )}
                {kind === 'fg' && (
                    <Text fz={13} c="dimmed">
                        Opens the finished-goods GRN form.
                    </Text>
                )}

                <Group justify="flex-end" gap="sm">
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!ready}
                        onClick={() =>
                            onContinue(kind === 'fg' ? { kind: 'fg' } : { kind: 'material', warehouseId: warehouseId as string })
                        }
                    >
                        Continue
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default CreateGrnTypeModal;
